import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initGA, trackPageView } from "@/lib/analytics";
import Home from "@/pages/Home";
import Divisions from "@/pages/Divisions";
import DivisionDetail from "@/pages/DivisionDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ComingSoon from "@/pages/ComingSoon";
import Sponsors from "@/pages/Sponsors";
import Tournaments from "@/pages/Tournaments";
import TournamentDetail from "@/pages/TournamentDetail";
import Videos from "@/pages/Videos";
import VideoWatch from "@/pages/VideoWatch";
import Merchandise from "@/pages/Merchandise";
import Links from "@/pages/Links";
import Captains from "@/pages/Captains";
import Champions from "@/pages/Champions";
import Promos from "@/pages/Promos";
import BraggersCup from "@/pages/BraggersCup";
import SpareList from "@/pages/SpareList";
import BraggersEdition from "@/pages/BraggersEdition";
import Orbit from "@/pages/Orbit";
import LiveTournament from "@/pages/LiveTournament";
import Notify from "@/pages/Notify";
import SubscribeResult from "@/pages/SubscribeResult";
import Login from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#555555]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    initGA();
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/divisions" element={<Divisions />} />
          <Route path="/divisions/:slug" element={<DivisionDetail />} />
          <Route path="/champions" element={<Champions />} />
          <Route path="/promos" element={<Promos />} />
          <Route path="/spare-list" element={<SpareList />} />
          <Route path="/spares" element={<Navigate to="/spare-list" replace />} />
          <Route path="/braggers" element={<BraggersCup />} />
          <Route path="/braggers/:slug" element={<BraggersEdition />} />
          <Route path="/orbit" element={<Orbit />} />
          <Route path="/tournaments/:ltid/live" element={<LiveTournament />} />
          <Route path="/tournaments/:slug" element={<TournamentDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/partners" element={<Sponsors />} />
          <Route path="/sponsors" element={<Navigate to="/partners" replace />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/videos/:slug" element={<VideoWatch />} />
          <Route path="/merchandise" element={<Merchandise />} />
          <Route path="/links" element={<Links />} />
          <Route path="/captains" element={<Captains />} />
          <Route path="/notify" element={<Notify />} />
          <Route path="/notify/confirmed" element={<SubscribeResult variant="confirmed" />} />
          <Route path="/notify/unsubscribed" element={<SubscribeResult variant="unsubscribed" />} />
          <Route path="/notify/error" element={<SubscribeResult variant="error" />} />
          <Route path="/admin/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
